import { useEffect, useMemo, useState } from "react";
import { X, Check, Star, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CityAutocomplete } from "./CityAutocomplete";
import { slug, prettyDate } from "@/lib/geo";

export const CountryPanel = ({ name, cities, visitedEntry, wishIndex, onClose, b }) => {
  const list = useMemo(() => cities.filter((c) => c.c === name).slice(0, 30).map((c) => c.n), [cities, name]);
  const been = useMemo(() => visitedEntry?.cities || [], [visitedEntry]);
  const [more, setMore] = useState([]);
  const [next, setNext] = useState(0);
  const [total, setTotal] = useState(0);
  const loadMore = (offset, limit) =>
    api.topCities(name, offset, limit).then((r) => { setTotal(r.total); setNext(offset + limit); setMore((m) => [...m, ...r.items]); }).catch(() => null);
  useEffect(() => { if (!b.readOnly) loadMore(0, 60); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Ignore taps in the first moment after opening, so the tap that opened the panel can't press a button in it
  const [armed, setArmed] = useState(false);
  useEffect(() => { const t = setTimeout(() => setArmed(true), 450); return () => clearTimeout(t); }, []);
  const guard = (fn) => () => { if (armed) fn(); };

  // Keep chip order stable while picking, so tapped cities never jump around
  const { all, placeOf } = useMemo(() => {
    const seen = new Set(list.map(slug));
    const extra = more.filter((c) => !seen.has(slug(c.name)) && seen.add(slug(c.name)));
    const custom = been.filter((n) => !seen.has(slug(n)));
    return {
      all: [...list, ...extra.map((c) => c.name), ...custom],
      placeOf: Object.fromEntries(extra.map((c) => [c.name, { name: c.name, x: c.x, y: c.y }])),
    };
  }, [list, more, been]);

  return (
    <div className="absolute left-3 right-3 bottom-3 max-h-[58%] sm:left-4 sm:right-auto sm:bottom-auto sm:top-4 sm:w-[340px] sm:max-h-[calc(100%-5rem)] overflow-y-auto overflow-x-visible rounded-2xl glass p-5 animate-pop" data-testid="country-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="eyebrow">{visitedEntry ? "Been there" : wishIndex >= 0 ? `Bucketlist #${wishIndex + 1}` : "Not visited yet"}</p>
          <h3 className="font-display text-2xl font-semibold mt-1" data-testid="country-panel-name">{name}</h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="country-panel-close" aria-label="Close"><X size={18} /></button>
      </div>
      {visitedEntry?.trips?.length > 0 && (
        <p className="font-mono text-[11px] text-emerald-300/80 mt-2" data-testid="country-panel-trips">
          {visitedEntry.trips.map((t) => prettyDate(t.date, "MMM yyyy")).join(" · ")}
        </p>
      )}
      {!b.readOnly && <div className="flex flex-wrap gap-2 mt-4">
        <Button size="sm" onClick={guard(() => b.toggleCountry(name))} data-testid="country-panel-toggle-visited"
          className={visitedEntry ? "bg-emerald-500/15 text-emerald-300 hover:bg-red-500/15 hover:text-red-300 border border-emerald-500/40" : "bg-emerald-500 text-[#04120c] hover:bg-emerald-400"}>
          <Check size={14} /> {visitedEntry ? "Visited · remove" : "I've been here"}
        </Button>
        {wishIndex < 0 && (
          <Button size="sm" variant="outline" onClick={guard(() => b.addWish(name))} data-testid="country-panel-add-wish" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 bg-transparent">
            <Star size={14} /> Add to bucketlist
          </Button>
        )}
      </div>}
      <p className="eyebrow mt-6 mb-2">{b.readOnly ? "Cities" : "Cities — tap the ones you've been to"}</p>
      <div className="flex flex-wrap gap-1.5">
        {all.map((c) => {
          const on = been.includes(c);
          return (
            <button key={c} disabled={b.readOnly} onClick={guard(() => b.toggleCity(name, c, placeOf[c]))} data-testid={`panel-city-${slug(c)}`}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors duration-200 ${on ? "bg-emerald-400 text-[#04120c] border-emerald-300 font-semibold shadow-[0_0_12px_rgba(52,211,153,0.45)]" : "border-white/10 text-slate-400 hover:text-white hover:border-white/30"}`}>
              {c}
            </button>
          );
        })}
        {all.length === 0 && <span className="text-xs text-slate-500">No major cities listed.</span>}
      </div>
      {!b.readOnly && next < total && (
        <button onClick={() => loadMore(next, 60)} className="mt-2 flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 transition-colors" data-testid="panel-more-cities">
          <ChevronDown size={13} /> Show more cities <span className="font-mono text-slate-500">({total - next} more)</span>
        </button>
      )}
      {!b.readOnly && <CityAutocomplete country={name} exclude={been} onPick={(city, place) => b.toggleCity(name, city, place)} />}
    </div>
  );
};
